/**
 * 해외 현장 경비 및 기타 운영성 비용 예산 신청서 (사용자 지시 2026-11-02 · docs/samples/work).
 *
 * <p>다른 서식처럼 「항목: 값」 표로 접지 않는다 — <b>회사 엑셀 양식 그대로</b>가 본문이다
 * (「자금청구_본사양식」 시트: 1. 외주기성 · 2. 자재기성 · 3. 소요경비 → 소계 → 합계).
 *
 * <h2>환율</h2>
 * 양식의 단위는 USD 이고, 현장은 현지 통화(KES · UZS · LAK …)로 돈을 쓴다. 엑셀 사례 시트가 하던 대로
 * 문서마다 <b>현지 통화 하나</b>와 「1 USD = ○ 현지」 를 적고, 줄마다 현지 금액을 적으면 USD 가 나온다
 * (사용자 결정 2026-11-02 「문서당 현지 통화 하나」). 현지 금액을 비운 줄은 USD 를 직접 적는다.
 *
 * <p>KES · UZS · LAK 는 수출입은행이 고시하지 않아 서버 환율 표(ref_exchange_rates)에 없다 — 그래서 사람이 적는다.
 * USD → 원화는 그 표의 그날 값을 기본으로 채우고 고칠 수 있다.
 *
 * <h2>금액은 저장하지 않는다</h2>
 * USD · 원화 · 소계 · 합계는 그릴 때 센다. 적힌 값(현지 금액 · 직접 적은 USD · 환율)만 남긴다 —
 * 계산한 값을 같이 넣어 두면 환율을 고친 뒤 옛 원화가 남는다. 결재 금액은 원화 합계다({@link budgetTotals}).
 */

import { stampDate, type ApprovalBox } from '@/pages/approval/paperForm';

export const OVERSEAS_BUDGET_FORM_KEY = 'OVERSEAS_BUDGET';

export type BudgetSectionKey = 'OUTSOURCE' | 'MATERIAL' | 'EXPENSE';

export const BUDGET_SECTIONS: Array<{ key: BudgetSectionKey; title: string; subtotal: string }> = [
  { key: 'OUTSOURCE', title: '1. 외주기성', subtotal: '외주기성 소 계' },
  { key: 'MATERIAL', title: '2. 자재기성', subtotal: '자재기성 소 계' },
  { key: 'EXPENSE', title: '3. 소요경비', subtotal: '소요경비 소 계' },
];

/**
 * 고르는 통화 — 해외 현장의 넷(사용자 지시 2026-11-03 「우즈벡 케냐 라오스 달러 다 되어야」).
 * 목록 밖의 값이 든 옛 문서는 고르개에 그 값을 함께 남긴다(OverseasBudgetControls).
 */
export const BUDGET_CURRENCIES: Array<{ code: string; label: string }> = [
  { code: 'USD', label: 'USD 미국 달러' },
  { code: 'KES', label: 'KES 케냐 실링' },
  { code: 'UZS', label: 'UZS 우즈베키스탄 숨' },
  { code: 'LAK', label: 'LAK 라오스 킵' },
  // 원화로도 적는다(사용자 지시 2026-11-03 「원화로도 입력이 되어야」). 1 USD = ○ KRW 는 원 환율과 같은 값이다.
  { code: 'KRW', label: 'KRW 원화' },
];

/**
 * 한 줄 — 양식의 「구분 · 공종명 · 금회 예정 · 내용 · 비고(품의 유/무)」.
 *
 * <p>{@code local} 은 현지 통화 금액, {@code usd} 는 현지 금액이 없을 때 직접 적은 USD 다.
 */
export type BudgetLine = {
  section: BudgetSectionKey;
  group: string;
  workName: string;
  local: string;
  usd: string;
  content: string;
  note: string;
};

export type OverseasBudget = {
  siteName: string;
  /** 「2026년 8월 1주차」 — 양식의 「금회 예정 (2026년 8월)」 머리에 선다. */
  period: string;
  /** 현지 통화 코드. USD 면 현지 칸이 서지 않는다. */
  currency: string;
  /** 1 USD = ○ 현지. */
  localPerUsd: string;
  /** 1 USD = ○ 원. */
  krwPerUsd: string;
  lines: BudgetLine[];
  /**
   * 줄 밖의 적는 칸 — 갈래 제목 줄 · 소계 · 합계의 내용 · 비고 칸(사용자 지시 2026-11-03 「소계에 내용 비고 이런 거도 다 입력」).
   * 열쇠는 {@link budgetCellKey}. 옛 문서에는 없다.
   */
  cells?: Record<string, string>;
};

/** 줄 밖 칸의 열쇠 — 「OUTSOURCE.head.content」 · 「MATERIAL.sub.note」 · 「TOTAL.sub.content」. */
export function budgetCellKey(scope: BudgetSectionKey | 'TOTAL', row: 'head' | 'sub', col: 'amount' | 'content' | 'note'): string {
  return `${scope}.${row}.${col}`;
}

const line = (section: BudgetSectionKey, group: string, workName = ''): BudgetLine => ({
  section, group, workName, local: '', usd: '', content: '', note: '',
});

export const emptyBudgetLine = (section: BudgetSectionKey): BudgetLine => line(section, '');

/** 엑셀 본사양식의 줄 그대로. */
export function defaultBudget(): OverseasBudget {
  return {
    siteName: '',
    period: '',
    currency: 'USD',
    localPerUsd: '',
    krwPerUsd: '',
    lines: [
      line('OUTSOURCE', '[직영성]'),
      line('OUTSOURCE', '[건축]'),
      line('OUTSOURCE', '[전기]'),
      line('OUTSOURCE', '[기계]'),
      line('MATERIAL', '[잡자재]'),
      line('MATERIAL', '[유류비]'),
      line('MATERIAL', '[운반비]'),
      ...['복리후생비', '지급수수료', '수도광열비', '지급임차료', '세금과공과', '여비교통비', '통신비', '소모품비', '차량유지비']
        .map((name) => line('EXPENSE', '', name)),
    ],
  };
}

const SECTION_KEYS = new Set<string>(BUDGET_SECTIONS.map((s) => s.key));

/** 무엇이 들어와도 예산표로 읽는다. 모양이 어긋난 값에 문서가 안 열리는 것보다 빈 표가 낫다. */
export function normalizeBudget(value: unknown): OverseasBudget | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const raw = value as Record<string, unknown>;
  const text = (v: unknown) => (v === null || v === undefined ? '' : String(v));
  const lines = Array.isArray(raw.lines)
    ? raw.lines
        .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
        .filter((row) => SECTION_KEYS.has(text(row.section)))
        .map((row) => ({
          section: text(row.section) as BudgetSectionKey,
          group: text(row.group),
          workName: text(row.workName),
          local: text(row.local),
          usd: text(row.usd),
          content: text(row.content),
          note: text(row.note),
        }))
    : [];
  return {
    siteName: text(raw.siteName),
    period: text(raw.period),
    currency: text(raw.currency).trim().toUpperCase() || 'USD',
    localPerUsd: text(raw.localPerUsd),
    krwPerUsd: text(raw.krwPerUsd),
    cells: raw.cells && typeof raw.cells === 'object' && !Array.isArray(raw.cells)
      ? Object.fromEntries(Object.entries(raw.cells as Record<string, unknown>).map(([k, v]) => [k, text(v)]))
      : {},
    lines,
  };
}

/** 현지 칸을 세울까 — 통화가 USD 가 아닐 때만. */
export function usesLocal(budget: OverseasBudget): boolean {
  return budget.currency !== '' && budget.currency !== 'USD';
}

function num(value: string): number | null {
  const cleaned = value.replace(/,/g, '').trim();
  if (cleaned === '') return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

/**
 * 한 줄의 USD — <b>센트로</b> 센다. 현지 금액이 있으면 환율로 나누고, 없으면 직접 적은 USD.
 * 센트에서 한 번 반올림해 두면 줄의 합과 소계가 화면에서 어긋나지 않는다.
 */
export function lineUsdCents(budget: OverseasBudget, row: BudgetLine): number | null {
  if (usesLocal(budget)) {
    const local = num(row.local);
    if (local !== null) {
      const rate = num(budget.localPerUsd);
      return rate && rate > 0 ? Math.round((local / rate) * 100) : null;
    }
  }
  const usd = num(row.usd);
  return usd === null ? null : Math.round(usd * 100);
}

/** USD 센트 → 원. 원 단위에서 한 번 접는다. 환율이 없으면 null. */
export function centsToKrw(budget: OverseasBudget, cents: number | null): number | null {
  const rate = num(budget.krwPerUsd);
  if (cents === null || !rate || rate <= 0) return null;
  return Math.round((cents / 100) * rate);
}

export type BudgetSum = { usdCents: number; krw: number | null };

/** 소계(갈래마다)와 합계. 원화는 <b>줄마다 접은 원</b>의 합이라 표의 숫자를 더한 값과 같다. */
/**
 * 한 줄의 원화 — 반올림 전 USD 에 원 환율을 곱해 <b>원에서 한 번만</b> 접는다. USD 센트를 먼저 접고 곱하면
 * 줄마다 몇 원씩 어긋난다(8,000,000 KES 가 83,200,012원으로 나왔다 · 2026-11-03).
 */
export function lineKrw(budget: OverseasBudget, row: BudgetLine): number | null {
  const krwRate = num(budget.krwPerUsd);
  if (!krwRate || krwRate <= 0) return null;
  let usd: number | null = null;
  if (budget.currency === 'KRW' && num(row.local) !== null) return Math.round(num(row.local)!);
  if (usesLocal(budget) && num(row.local) !== null) {
    const rate = num(budget.localPerUsd);
    usd = rate && rate > 0 ? num(row.local)! / rate : null;
  } else {
    usd = num(row.usd);
  }
  return usd === null ? null : Math.round(usd * krwRate);
}

export function budgetTotals(budget: OverseasBudget): { sections: Record<BudgetSectionKey, BudgetSum>; total: BudgetSum } {
  const hasKrw = num(budget.krwPerUsd) !== null && (num(budget.krwPerUsd) ?? 0) > 0;
  const empty = (): BudgetSum => ({ usdCents: 0, krw: hasKrw ? 0 : null });
  const sections: Record<BudgetSectionKey, BudgetSum> = { OUTSOURCE: empty(), MATERIAL: empty(), EXPENSE: empty() };
  const total = empty();
  for (const row of budget.lines) {
    const cents = lineUsdCents(budget, row);
    if (cents === null) continue;
    const krw = lineKrw(budget, row) ?? 0;
    const bucket = sections[row.section];
    bucket.usdCents += cents;
    total.usdCents += cents;
    if (bucket.krw !== null) bucket.krw += krw;
    if (total.krw !== null) total.krw += krw;
  }
  return { sections, total };
}

/** 결재 금액 — 원화 합계. 환율이 비어 있으면 금액이 없다. */
export function budgetAmount(value: unknown): number | undefined {
  const budget = normalizeBudget(value);
  if (!budget) return undefined;
  return budgetTotals(budget).total.krw ?? undefined;
}

/**
 * 시트 제목 — 엑셀 A1 「OOO현장 26년 8월 1주차 운전자금 예산 신청서 (직영 및 경비성 항목)」.
 * 이 서식의 종이는 레터헤드·결재란 없이 시트 그대로라(사용자 지시 2026-11-03), 문서 제목도 이 값에서 짓는다.
 */
export function budgetSheetTitle(budget: OverseasBudget): string {
  const site = budget.siteName.trim();
  const place = site === '' ? 'OOO현장' : site.endsWith('현장') ? site : `${site} 현장`;
  const period = budget.period.trim();
  return `${place}${period ? ` ${period}` : ''} 운전자금 예산 신청서 (직영 및 경비성 항목)`;
}

/** 엑셀의 갈래 이름 칸(「[직영성]」 「[유류비]」) — 진회색으로 칠하는 칸. */
export function isGroupLabel(text: string): boolean {
  return /^\[.*\]$/.test(text.trim());
}

/**
 * 「금회 예정 (2026년 8월)」 머리의 괄호 — 기간에서 <b>월까지만</b> 뽑는다(사용자 지시 2026-11-03).
 * 「2026년 8월 1주차」 · 「26년 8월 1주차」 → 「2026년 8월」. 연·월을 못 읽으면 적힌 기간 그대로.
 */
export function periodMonth(period: string): string {
  const m = period.match(/(\d{2,4})\s*년\s*(\d{1,2})\s*월/);
  if (!m) return period.trim();
  const year = m[1]!.length === 2 ? `20${m[1]}` : m[1]!;
  return `${year}년 ${Number(m[2])}월`;
}

/**
 * 서버 환율 표에서 이 문서의 환율을 짓는다 — 사람이 적지 않는다(사용자 지시 2026-11-03 「이미 있잖아」).
 *
 * <p>표(ref_exchange_rates)는 「단위당 원화」 다(USD 1, KES 100, LAK · UZS 1000). USD → 원은 그대로,
 * 1 USD 가 몇 현지 통화인가는 두 원화 값을 나눠 낸다. 그 통화가 표에 없으면 null — 화면은 「-」 로 둔다.
 * 지은 값은 문서에 함께 저장돼, 상신한 날의 환율이 그 문서의 환율로 남는다.
 */
export function ratesFromMarket(
  rates: Array<{ currency: string; unit: number; rate: number }>,
  currency: string,
): { krwPerUsd: string; localPerUsd: string } | null {
  const perUnit = (code: string) => {
    const row = rates.find((r) => r.currency === code);
    return row && row.rate > 0 ? row.rate / (row.unit || 1) : null;
  };
  const usd = perUnit('USD');
  if (usd === null) return null;
  if (currency === '' || currency === 'USD') return { krwPerUsd: String(usd), localPerUsd: '' };
  if (currency === 'KRW') return { krwPerUsd: String(usd), localPerUsd: String(usd) };
  const local = perUnit(currency);
  if (local === null) return null;
  // 자르지 않는다 — 넷째 자리에서 자르면 큰 금액에서 원이 어긋난다. 화면은 넷째 자리까지만 보인다.
  return { krwPerUsd: String(usd), localPerUsd: String(usd / local) };
}

/**
 * 기간 고르개의 「월」 목록 — 앞뒤 6개월(사용자 지시 2026-11-03 「기간 입력도 드롭박스로」). 「2026년 8월」 꼴.
 */
export function periodMonthOptions(today = new Date()): string[] {
  const out: string[] = [];
  for (let offset = -6; offset <= 6; offset++) {
    const d = new Date(today.getFullYear(), today.getMonth() + offset, 1);
    out.push(`${d.getFullYear()}년 ${d.getMonth() + 1}월`);
  }
  return out;
}

/** 기간 고르개의 「주차」 목록. 빈 값은 「월 전체」. */
export const PERIOD_WEEKS = ['1주차', '2주차', '3주차', '4주차', '5주차'];

/** 「2026년 8월 1주차」 → 월 · 주차. 못 읽으면 월은 {@link periodMonth} 값, 주차는 빈 값. */
export function splitPeriod(period: string): { month: string; week: string } {
  const week = period.match(/(\d)\s*주차/);
  return { month: periodMonth(period), week: week ? `${week[1]}주차` : '' };
}

/**
 * 종이에 찍히는 금액 — <b>고른 통화 그대로</b>(사용자 지시 2026-11-03 「달러로 체크하고 썼으면 소계에서는 USD 5000」).
 * 엑셀 금액 칸 서식이 「[$KES] #,##0」 이라 원본도 통화 코드를 앞에 붙인 그 통화 값이다. USD · 원화 환산은 결재 금액과
 * 대장이 쓴다({@link budgetTotals}).
 */
export function lineEntered(budget: OverseasBudget, row: BudgetLine): number | null {
  return num(usesLocal(budget) ? row.local : row.usd);
}

/** 고른 통화로 센 소계 · 합계. 센트(1/100) 단위로 더해 소수 더하기 오차를 막는다. */
export function enteredTotals(budget: OverseasBudget): { sections: Record<BudgetSectionKey, number>; total: number } {
  const hund = { OUTSOURCE: 0, MATERIAL: 0, EXPENSE: 0 } as Record<BudgetSectionKey, number>;
  let total = 0;
  for (const row of budget.lines) {
    const v = lineEntered(budget, row);
    if (v === null) continue;
    const h = Math.round(v * 100);
    hund[row.section] += h;
    total += h;
  }
  return {
    sections: { OUTSOURCE: hund.OUTSOURCE / 100, MATERIAL: hund.MATERIAL / 100, EXPENSE: hund.EXPENSE / 100 },
    total: total / 100,
  };
}

/** 「USD 5,000」 · 「KES 8,000,000」 · 「USD 1,234.5」 — 소수는 있을 때만(둘째 자리까지). */
export function formatMoney(code: string, value: number | null): string {
  if (value === null) return '';
  const text = value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return `${code || 'USD'} ${text}`;
}

/**
 * 통화를 바꾼다 — 적어 둔 숫자는 그대로 따라간다. 값이 통화마다 다른 칸(local · usd)에 담겨 있어,
 * 옮기지 않으면 통화를 고쳐 고른 순간 표의 숫자가 비어 보인다.
 */
export function switchCurrency(budget: OverseasBudget, currency: string): OverseasBudget {
  const toLocal = currency !== '' && currency !== 'USD';
  return {
    ...budget,
    currency,
    lines: budget.lines.map((row) => {
      const value = row.local.trim() !== '' ? row.local : row.usd;
      return toLocal ? { ...row, local: value, usd: '' } : { ...row, usd: value, local: '' };
    }),
  };
}

export function formatUsd(cents: number | null): string {
  if (cents === null) return '';
  const minus = cents < 0 ? '-' : '';
  const abs = Math.abs(cents);
  const whole = Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `${minus}${whole}.${String(abs % 100).padStart(2, '0')}`;
}

export function formatKrw(krw: number | null): string {
  return krw === null ? '' : krw.toLocaleString('ko-KR');
}

/** 결재칸 한 칸 — 엑셀 그림의 「현장 결재」 · 「본사 결재」 칸. */
export type BudgetSignCell = {
  label: string;
  name: string | null;
  stamp: string | null;
  date: string;
  signImage: string | null;
};

/**
 * 결재선을 엑셀 결재칸 두 개에 앉힌다 (사용자 결정 2026-11-03).
 *
 * <pre>
 *   현장 결재 : 담당 · 소장                 ← 기안자 · 직책에 「소장」
 *   본사 결재 : 담당 · 팀장 · 임원 · 대표이사 ← 담당은 기안자, 나머지는 직책으로 가른다. 본부장은 임원 칸
 * </pre>
 *
 * <p>담당은 현장 · 본사 두 칸 모두 기안자다. 대표이사 칸이 비고 전결 줄이 있으면 그 칸에 「전결」 이 선다 — 본부장 전결로
 * 끝나는 문서다. 직책으로 못 가른 결재자는 칸이 없고 「처리 이력」 이 보여 준다.
 */
export function budgetApprovalCells(boxes: ApprovalBox[]): { site: BudgetSignCell[]; hq: BudgetSignCell[] } {
  const cell = (label: string, box?: ApprovalBox): BudgetSignCell => ({
    label,
    name: box?.name ?? null,
    stamp: box?.signature ?? null,
    date: box ? stampDate(box.decidedAt) : '',
    signImage: box?.signImage ?? null,
  });
  const lines = boxes.filter((b) => (b.kind === 'APPROVAL' || b.kind === 'AGREEMENT') && !b.skipped);
  const pos = (b: ApprovalBox) => (b.position ?? '').replace(/\s*전결$/, '');
  const used = new Set<ApprovalBox>();
  const take = (test: (p: string) => boolean) => {
    const found = lines.find((b) => !used.has(b) && test(pos(b)));
    if (found) used.add(found);
    return found;
  };
  const ceo = take((p) => p.includes('대표'));
  const exec = take((p) => /본부장|임원|이사|상무|전무|부사장/.test(p));
  const lead = take((p) => p.includes('팀장'));
  const chief = take((p) => p.includes('소장'));
  // 담당은 두 칸 다 <b>작성한 사람</b>이다(사용자 지시 2026-11-03 「담당이 2칸인데 둘 다 작성한 담당자」).
  const drafter = boxes.find((b) => b.kind === 'DRAFT');

  const ceoCell = cell('대표이사', ceo);
  if (!ceo && lines.some((b) => b.final)) ceoCell.name = '전결';

  return {
    site: [cell('담 당', drafter), cell('소 장', chief)],
    hq: [cell('담 당', drafter), cell('팀 장', lead), cell('임 원', exec), ceoCell],
  };
}
